import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock, User, Users, ChevronRight, ChevronDown, LogOut, HelpCircle, MessageSquare, Smartphone, Mail, ShieldCheck, X, Check } from "lucide-react";
import QRCode from "react-qr-code";
import { api, fileUrl, formatErr } from "@/lib/api";
import { getGuest, isIpadMode, switchActiveGuest, signOutGuest, updateRoster } from "@/lib/guest";
import { readFresh, readAny, prime } from "@/lib/prefetch";
import { prefetchImage, startGuestBackgroundSync } from "@/lib/prefetch";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { useIpadLive } from "@/lib/useIpadLive";
import { useTrackView } from "@/lib/analyticsTracker";
import { useAppTz, dayKeyInTz, fmtTime } from "@/lib/tz";
import GuestHeader from "@/components/GuestHeader";
import JourneyTimeline from "@/components/JourneyTimeline";
import PendingInviteChip from "@/components/PendingInviteChip";
import GuestNotificationChip from "@/components/GuestNotificationChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBranding } from "@/lib/branding";

// Safety-net polling cadence for the shared cabin iPad. The primary
// sync channel is the iPad WebSocket (see useIpadKillWatcher +
// ipadLiveBus) — this interval only runs as a fallback for the rare
// case where the socket has silently dropped and no push has arrived
// in the last ~30 s. 10 s was aggressive; 30 s is polite on the
// upstream while still keeping the shared view honest.
const REFRESH_MS = 30000;

// Timezone-anchored bookings grouping — was `.toISOString().slice(0, 10)`
// (UTC date) which could bucket a "23:30 local" booking on the wrong day
// for guests east of UTC. Now delegates to the shared `dayKeyInTz` so
// grouping matches what the Spa iPad calendar shows. Same treatment as
// SpaOpsView.jsx (iter173).
function dayKey(iso, tz = null) {
  return dayKeyInTz(iso, tz);
}
function dayLabel(iso, tz = null) {
  const zone = tz || undefined;
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  // Compare via dayKeyInTz so "Today"/"Tomorrow" honours the yacht's tz
  // instead of the browser's — matters when a guest connects from a laptop
  // in another timezone the morning of their arrival.
  const tk = dayKeyInTz(iso, zone);
  const todayK = dayKeyInTz(today.toISOString(), zone);
  const tomorrowK = dayKeyInTz(tomorrow.toISOString(), zone);
  if (tk === todayK) return "Today";
  if (tk === tomorrowK) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
function timeLabel(iso, tz = null) { return fmtTime(iso, tz); }

export default function CabinView() {
  const navigate = useNavigate();
  const appTz = useAppTz();
  useTrackView("cabin");
  // `getGuest()` is called ONCE inside these initialisers so we can
  // hydrate `session` from the prefetch cache primed by GuestLanding
  // without hoisting the outer `const g` above the useState calls
  // (that reordering would break every other hook downstream that
  // depends on `g`). The full `g` binding is created below and reads
  // the same source of truth.
  const _initialG = getGuest();
  const _initialCacheKey = _initialG?.cabin_id ? `ipad:session:${_initialG.cabin_id}` : null;
  const [session, setSession] = useState(() => _initialCacheKey ? (readAny(_initialCacheKey) || null) : null);
  const [loading, setLoading] = useState(() => !(_initialCacheKey && readAny(_initialCacheKey)));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mobileUrl, setMobileUrl] = useState("");
  const [resumeToken, setResumeToken] = useState("");   // short-lived JWT for the QR
  const [resumeErr, setResumeErr] = useState(false);

  const g = getGuest();
  const { journeyPortMapEnabled } = useBranding();

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    if (!isIpadMode() || !g.cabin_id || !g.ipad_token) {
      navigate("/");
      return;
    }
    try {
      const { data } = await api.post("/public/ipad/session", { cabin_id: g.cabin_id, session_token: g.ipad_token });
      setSession(data);
      updateRoster(data.cabin_roster || []);
      // Refresh the cached copy so the next visibility-change tick +
      // any future remounts read the newest session too.
      try { prime(`ipad:session:${g.cabin_id}`, data); } catch { /* prime best-effort */ }
      // Warm the cabin cover so the masked hero paints from cache on
      // route transitions. Also fire the treatments prefetch — the
      // shared iPad's next tap is almost always "Book a treatment",
      // and the tiles need their photos ready by then.
      if (data.cabin_cover_image_path) prefetchImage(data.cabin_cover_image_path);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        toast.error("iPad session ended. Please log in again.");
        signOutGuest();
        navigate("/");
        return;
      }
      // Background polls (silent=true) run every 30s + on tab visibility change.
      // A single transient network blip used to spam the guest with a toast every
      // 30s. Only surface load errors on the initial / user-triggered load so the
      // shared iPad stays serene during momentary WiFi hiccups.
      if (!silent) {
        toast.error(formatErr(e.response?.data?.detail) || "Could not reach the yacht spa.");
      } else {
        // eslint-disable-next-line no-console
        console.debug("[CabinView] silent poll failed:", e?.message || e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), REFRESH_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    // Pull the Developer-set guest mobile URL for the QR block.
    api.get("/public/settings").then(({ data }) => setMobileUrl((data.guest_mobile_url || "").trim())).catch(() => {});    // Mint (and periodically refresh) the 1-tap resume token so the QR keeps
    // working while the iPad is left on for the whole charter. Token TTL is
    // 10 min server-side; we refresh every 8 min to stay safely ahead.
    const mintResume = async () => {
      if (!isIpadMode() || !g.cabin_id || !g.ipad_token) return;
      try {
        const { data } = await api.post("/public/ipad/mobile-resume-token", {
          cabin_id: g.cabin_id, session_token: g.ipad_token,
        });
        setResumeToken(data.resume_token || "");
        setResumeErr(false);
      } catch { setResumeErr(true); }
    };
    mintResume();
    const rt = setInterval(mintResume, 8 * 60 * 1000);
    // Start the background revalidator so treatment / crew content
    // stays fresh + images stay warm even while the iPad idles on
    // /cabin (the most common resting state). Runs one immediate
    // warm-up too so a freshly-reloaded kiosk gets everything ready
    // before the guest taps anything.
    const stopBg = startGuestBackgroundSync(60_000);
    // NOTE: the "kick iPad" WS + poll listener used to live here but
    // only ran while the guest was on CabinView. It's now mounted at
    // the App root via `useIpadKillWatcher` so every route hears the
    // eviction command — see /app/frontend/src/lib/useIpadKillWatcher.js.
    return () => {
      clearInterval(t); clearInterval(rt);
      if (stopBg) stopBg();
      document.removeEventListener("visibilitychange", onVis);
    };
    /* eslint-disable-next-line */
  }, []);

  const grouped = useMemo(() => {
    if (!session?.bookings) return [];
    const byDay = {};
    for (const b of session.bookings) {
      const k = dayKey(b.start_at, appTz);
      (byDay[k] = byDay[k] || []).push(b);
    }
    // Merge couples: two bookings sharing couple_booking_id → one composite row.
    const out = Object.keys(byDay).sort().map((k) => {
      const list = byDay[k];
      const seen = new Set();
      const merged = [];
      for (const b of list) {
        if (b.couple_booking_id) {
          if (seen.has(b.couple_booking_id)) continue;
          seen.add(b.couple_booking_id);
          const pair = list.filter((x) => x.couple_booking_id === b.couple_booking_id);
          const primary = pair[0];
          const partner = pair[1] || null;
          merged.push({
            ...primary,
            isCouple: !!partner,
            guest_name: partner ? `${primary.guest_name} & ${partner.guest_name}` : primary.guest_name,
          });
        } else {
          merged.push({ ...b, isCouple: false });
        }
      }
      merged.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
      return { day: k, iso: list[0].start_at, items: merged };
    });
    return out;
  }, [session]);

  const bookNew = () => setPickerOpen(true);

  // Live sync — when a manager edits a guest / trip in Trips & guests, or
  // when a disembark date passes and the backend re-computes the roster,
  // fanout arrives via WebSocket. Silent refetch so the iPad never shows a
  // just-disembarked guest as still onboard.
  useLiveEvents((ev) => {
    if (!ev) return;
    if (ev.type === "charter.updated" || ev.type === "guest.onboard_changed") {
      load(true);
    }
  });

  // Same story on the iPad-scoped socket — this is the channel that
  // actually reaches the shared kiosk (dashboard WS refuses guest
  // connections). Any charter / booking / cabin update pushed by the
  // backend triggers a silent refetch, so the roster + upcoming
  // appointment list stay live without the guest tapping anything.
  useIpadLive((ev) => {
    if (!ev?.type) return;
    if (
      ev.type === "charter.updated" ||
      ev.type === "guest.onboard_changed" ||
      ev.type === "cabin.updated" ||
      ev.type === "content.updated" ||
      ev.type === "settings.updated" ||
      ev.type.startsWith("booking.")
    ) {
      load(true);
    }
  });

  const pickAndGo = (guest_id, dest = "/experience") => {
    switchActiveGuest(guest_id);
    setPickerOpen(false);
    navigate(dest);
  };

  const doSignOut = () => {
    if (!window.confirm("Sign this iPad out of the cabin?")) return;
    signOutGuest();
    navigate("/");
  };

  if (loading) return (
    <div className="min-h-screen bg-primary text-background flex items-center justify-center p-8" data-testid="cabin-view-loading">
      <div className="w-full max-w-md text-center">
        <div className="overline text-accent mb-3">Preparing your cabin</div>
        <h2 className="font-display text-2xl sm:text-3xl mb-6">Loading your dashboard…</h2>
        <div className="h-1.5 bg-background/15 rounded-full overflow-hidden">
          <div className="h-full bg-accent animate-pulse w-2/3" />
        </div>
        <div className="mt-3 text-[11px] uppercase tracking-widest text-background/50">
          Syncing roster · bookings · journey
        </div>
      </div>
    </div>
  );
  if (!session) return null;

  // Standby — the backend accepted the iPad's PIN + session token but
  // no active charter has this cabin on its guest list yet. Render a
  // polite "your journey begins soon" screen and let the 30 s poll +
  // WebSocket sync flip us out of this state the moment a manager
  // assigns guests. iPads deliberately stay signed in so the crew can
  // provision the device between trips without re-typing the PIN.
  if (session.standby) {
    const coverPath = session.cabin_cover_image_path || null;
    return (
      <div className="min-h-screen relative overflow-hidden bg-primary text-background" data-testid="cabin-view-standby">
        {coverPath && (
          <div className="absolute inset-0 z-0" aria-hidden>
            <img
              src={fileUrl(coverPath)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-primary/75" />
          </div>
        )}
        <div className="relative z-10 min-h-screen flex flex-col">
          <GuestHeader />
          <main className="flex-1 flex items-center justify-center px-6 md:px-10 pb-24">
            <div className="w-full max-w-xl text-center">
              <div className="overline text-accent mb-4" data-testid="standby-overline">
                {session.cabin_name || "This cabin"} · Standby
              </div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-white mb-6">
                Your journey <em className="font-serif italic text-accent">begins soon</em>.
              </h1>
              <p className="text-white/75 text-base md:text-lg leading-relaxed mb-10 max-w-md mx-auto">
                This iPad is ready and connected to the yacht. Once the crew
                confirms your voyage, your cabin dashboard will appear here
                automatically — no need to sign in again.
              </p>
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-white/25 bg-white/5 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5" aria-hidden>
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
                </span>
                <span className="text-xs uppercase tracking-widest text-white/60">
                  Listening for your charter
                </span>
              </div>
              <div className="mt-10 text-[11px] uppercase tracking-widest text-white/40">
                Est. 2026 · Onboard wellness
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const roster = session.cabin_roster || [];
  // Cover photo is now painted globally by <IpadThemeMount /> so every guest
  // route (menu, booking, reservations, cabin) shares the same masked
  // background. We only need the boolean flag here to keep the per-page
  // typography helpers below in the light-on-dark variant.
  const onCover = isIpadMode() && !!(session.cabin_cover_image_path);
  const headText = onCover ? "text-white" : "";
  const subText = onCover ? "text-white/75" : "text-muted-foreground";
  const faintText = onCover ? "text-white/60" : "text-muted-foreground";
  const overlineCls = onCover ? "text-white/60" : "text-primary/60";
  const glassCard = onCover
    ? "bg-white/10 backdrop-blur-md border border-white/25 hover:border-white/60 text-white"
    : "bg-card border border-border hover:border-primary";
  const glassPanel = onCover
    ? "bg-white/10 backdrop-blur-md border border-white/25 text-white"
    : "bg-card border border-border";
  const dashedEmpty = onCover
    ? "border border-dashed border-white/40 text-white/70"
    : "border border-dashed border-border text-muted-foreground";

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="cabin-view">
      <div className="relative z-10">
      <GuestHeader />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="overline text-accent mb-2">Cabin view · shared iPad</div>
            <h1 className={`font-display text-4xl sm:text-5xl leading-tight ${headText}`}>{session.cabin_name}</h1>
            <p className={`text-sm mt-2 flex items-center gap-2 ${subText}`}>
              <Users className="h-4 w-4" /> {roster.length} guest{roster.length !== 1 ? "s" : ""} in this cabin
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={bookNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid="cabin-book-new">
              Book a treatment <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            {/* Guest iPads deliberately don't expose a Sign out button any
                more — only Admin+ can kick the device from the Cabins
                panel. This prevents a curious guest from logging the
                kiosk out mid-charter. */}
            {!isIpadMode() && (
              <Button variant="outline" onClick={doSignOut} className="rounded-full" data-testid="cabin-signout">
                <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
              </Button>
            )}
          </div>
        </div>

        {/* Persistent guest email · GDPR-scoped */}
        <GuestEmailPanel
          roster={roster}
          cabinId={g.cabin_id}
          sessionToken={g.ipad_token}
          onCover={onCover}
          overlineCls={overlineCls}
          headText={headText}
          subText={subText}
          faintText={faintText}
          glassPanel={glassPanel}
          onRosterUpdate={(next) => {
            updateRoster(next);
            setSession((prev) => prev ? { ...prev, cabin_roster: next } : prev);
          }}
        />

        {/* Cabin guests row */}
        <section className="mb-10" data-testid="cabin-guests">
          <div className={`overline mb-3 ${overlineCls}`}>Guests</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {roster.map((r) => (
              <button
                key={r.guest_id}
                onClick={() => pickAndGo(r.guest_id, "/reservations")}
                className={`p-4 text-left transition-colors ${glassCard}`}
                data-testid={`guest-tile-${r.guest_id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-accent" />
                  <span className="font-medium truncate">{r.guest_name}</span>
                </div>
                <div className={`text-xs ${faintText}`}>
                  {(session.bookings || []).filter((b) => b.guest_id === r.guest_id && b.status !== "cancelled").length} upcoming
                </div>
                <div className="text-[10px] text-accent mt-2">Open my reservations →</div>
              </button>
            ))}
          </div>
        </section>

        {/* Journey timeline — same proportional line the guests see on /journey,
            rendered here so everyone in the cabin can see the voyage's shape
            (embark → intermediate ports → disembark) without having to sign
            into their personal page. Falls back gracefully to a two-node
            line when the manager hasn't made the itinerary public.

            Compact rendering for the iPad tile: the "ABOARD / Days remaining"
            copy is lifted into the top-right corner of the panel and the
            timeline uses reduced vertical rhythm so the whole card sits as
            an elegant strip. */}
        {(session.embark_port || session.disembark_port || session.embark_at || session.disembark_at || (session.itinerary_stops || []).length > 0) && (() => {
          // Days-remaining copy replicates JourneyTimeline's `centerCopy`
          // logic so the top-right label always agrees with the marker on
          // the line — kept local to avoid pushing state into the shared
          // component just for this one caller.
          const eAt = session.embark_at ? new Date(session.embark_at).getTime() : null;
          const dAt = session.disembark_at ? new Date(session.disembark_at).getTime() : null;
          const now = Date.now();
          let corner = { label: "Aboard", value: null };
          if (eAt && eAt > now) {
            const d = Math.ceil((eAt - now) / 86_400_000);
            corner = { label: "Boarding in", value: `${d} ${d === 1 ? "day" : "days"}` };
          } else if (dAt && dAt < now - 86_400_000) {
            corner = { label: "Voyage complete", value: "safe travels" };
          } else if (dAt) {
            const dr = Math.max(0, Math.ceil((dAt - now) / 86_400_000));
            corner = dr === 0
              ? { label: "Final day", value: "today" }
              : { label: "Days remaining", value: `${dr} ${dr === 1 ? "day" : "days"}` };
          }
          return (
            <section className={`mb-10 px-5 md:px-6 pt-4 pb-3 md:pb-4 ${glassPanel}`} data-testid="cabin-journey">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className={`overline ${overlineCls}`}>Voyage · {(session.itinerary_stops || []).length > 0 ? "your itinerary" : "charter window"}</div>
                <div className="text-right leading-tight" data-testid="cabin-journey-corner">
                  <div className="overline text-accent text-[10px]">{corner.label}</div>
                  {corner.value && (
                    <div className={`font-display text-sm md:text-base tracking-tight mt-0.5 whitespace-nowrap ${headText}`}>
                      {corner.value}
                    </div>
                  )}
                </div>
              </div>
              <JourneyTimeline
                embarkPort={session.embark_port}
                embarkAt={session.embark_at}
                disembarkPort={session.disembark_port}
                disembarkAt={session.disembark_at}
                // Cabin iPad is a crew-provisioned surface inside a
                // physical cabin — always show the full charter
                // itinerary here. The `itinerary_public` flag stays
                // meaningful for the guest's personal `/journey` page
                // (different endpoint), NOT for this shared view.
                stops={session.itinerary_stops || []}
                filterStopsToWindow={false}
                hideCopy
                compact
                portMapEnabled={journeyPortMapEnabled}
              />
            </section>
          );
        })()}

        {/* Common calendar */}
        <section data-testid="cabin-calendar">
          {/* Cabin-wide notifications & pending invites (any roster member). */}
          {(session.cabin_roster || []).map((r) => (
            <div key={r.guest_id} className="mb-3 space-y-2">
              <GuestNotificationChip guestId={r.guest_id} />
              <PendingInviteChip guestId={r.guest_id} />
            </div>
          ))}
          <div className={`overline mb-3 ${overlineCls}`}>Upcoming — all guests</div>
          {grouped.length === 0 ? (
            <div className={`p-10 text-center ${dashedEmpty}`}>
              No upcoming bookings yet. Tap <span className={`font-medium ${onCover ? "text-white" : "text-primary"}`}>Book a treatment</span> to schedule one.
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map(({ day, iso, items }) => (
                <div key={day}>
                  <div className="flex items-baseline gap-3 mb-3">
                    <CalendarDays className="h-4 w-4 text-accent" />
                    <h3 className={`font-display text-xl ${headText}`}>{dayLabel(iso)}</h3>
                    <span className={`text-xs ${faintText}`}>{items.length} appointment{items.length !== 1 ? "s" : ""}</span>
                  </div>
                  <ul className="space-y-2">
                    {items.map((b) => (
                      <li
                        key={b.id}
                        className={`p-4 flex items-start gap-4 ${b.isCouple
                          ? (onCover ? "bg-accent/25 backdrop-blur-md border border-accent/60 text-white" : "bg-accent/10 border border-accent/40")
                          : glassPanel}`}
                        data-testid={`cabin-appt-${b.id}`}
                      >
                        <div className="text-center min-w-[70px]">
                          <div className={`font-display text-2xl ${onCover ? "text-white" : "text-primary"}`}>{timeLabel(b.start_at, appTz)}</div>
                          <div className={`text-[10px] uppercase tracking-widest ${faintText}`}>{b.duration_min} min</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium flex items-center gap-2 flex-wrap">
                            <span>{b.guest_name}</span>
                            {b.isCouple && <span className="text-[10px] text-accent uppercase tracking-widest">♥ Couples</span>}
                          </div>
                          <div className={`text-sm ${subText}`}>{b.treatment_name}</div>
                          {b.notes && (
                            <div className={`mt-2 text-xs flex items-start gap-1 ${subText}`}>
                              <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0 text-accent" />
                              <span className="whitespace-pre-wrap">{b.notes}</span>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Connect from your mobile — QR + per-guest PIN codes */}
        {mobileUrl && (
          <section className={`mt-12 p-6 md:p-8 ${glassPanel}`} data-testid="cabin-mobile-qr">
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 items-start">
              <div className="flex flex-col items-center">
                <div className="bg-white p-3 border border-border" data-testid="cabin-mobile-qr-image">
                  <QRCode
                    value={resumeToken
                      ? `${mobileUrl}${mobileUrl.includes("?") ? "&" : "?"}resume=${resumeToken}`
                      : mobileUrl}
                    size={180}
                    level="M"
                    style={{ height: 180, width: 180 }}
                  />
                </div>
                <div className={`mt-3 flex items-center gap-1.5 overline text-[10px] ${overlineCls}`}>
                  <Smartphone className="h-3 w-3" /> {resumeToken ? "Scan for 1-tap sign-in" : "Scan with your phone"}
                </div>
                {mobileUrl && (
                  <a
                    href={mobileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 text-[11px] text-accent underline decoration-dotted underline-offset-2 break-all font-mono max-w-[200px] text-center"
                    data-testid="cabin-mobile-qr-url"
                  >
                    {mobileUrl}
                  </a>
                )}
                {resumeErr && (
                  <div className="text-[10px] text-destructive mt-1 max-w-[180px] text-center" data-testid="qr-resume-err">
                    Falling back to manual login — check your iPad session.
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="overline text-accent mb-1">Bring your own device</div>
                <h3 className={`font-display text-2xl md:text-3xl leading-tight ${headText}`}>Prefer to book from your mobile?</h3>
                <p className={`text-sm mt-2 leading-relaxed ${subText}`}>
                  {resumeToken
                    ? "Just scan — your phone is auto-signed in without a PIN. Bookings stay in sync across this iPad and any device you sign in from."
                    : "Point your phone's camera at the QR — the spa app opens in your browser. Log in with your cabin and the personal PIN listed below. Your bookings stay in sync across this iPad and any device you sign in from."}
                </p>
                <div className="mt-5">
                  <div className={`overline text-[10px] mb-2 ${overlineCls}`}>Your PIN codes · {session.cabin_name}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cabin-mobile-pins">
                    {roster.map((r) => (
                      <div
                        key={r.guest_id}
                        className={`p-3 flex items-center justify-between gap-3 ${onCover ? "bg-white/10 border border-white/25" : "border border-border"}`}
                        data-testid={`cabin-mobile-pin-${r.guest_id}`}
                      >
                        <div className="min-w-0">
                          <div className={`text-[10px] overline ${onCover ? "text-white/50" : "text-primary/50"}`}>
                            {r.female ? "Ms" : "Guest"}
                          </div>
                          <div className="text-sm font-medium truncate">{r.guest_name}</div>
                        </div>
                        <div className="font-mono text-lg tracking-[0.35em] text-accent select-all">
                          {r.pin || "— — — —"}
                        </div>
                      </div>
                    ))}
                    {roster.length === 0 && (
                      <div className={`col-span-full text-xs italic ${faintText}`}>No guests are registered in this cabin yet.</div>
                    )}
                  </div>
                  <p className={`text-[11px] mt-3 italic ${faintText}`}>
                    PIN codes are personal — please don't share them outside this cabin.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      </div>

      {/* Roster picker for "Book a treatment" */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-primary/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={() => setPickerOpen(false)}>
          <div className="bg-background border border-border max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="cabin-roster-picker">
            <div className="overline text-accent mb-2">Who is this booking for?</div>
            <h3 className="font-display text-2xl mb-6">Pick a guest</h3>
            <div className="space-y-2">
              {roster.map((r) => (
                <button
                  key={r.guest_id}
                  onClick={() => pickAndGo(r.guest_id, "/experience")}
                  className="w-full border border-border hover:border-primary p-4 text-left transition-colors flex items-center gap-3"
                  data-testid={`cabin-pick-${r.guest_id}`}
                >
                  <User className="h-4 w-4 text-accent" />
                  <span className="font-medium">{r.guest_name}</span>
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={() => setPickerOpen(false)} className="w-full mt-4">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GuestEmailPanel · GDPR-consent-scoped, per-charter email capture.
//
// Sits on the shared cabin iPad and offers each roster member a one-time,
// per-trip email input for automated booking notifications. Emails are
// stored ONLY on `charters.guests[].email` for the duration of the trip and
// are expunged when the charter is deleted. Never tied to identifying
// personal data and never surfaced in operator logs (see `mask_email` on
// the backend). Any guest can overwrite or clear their address at any time.
// ─────────────────────────────────────────────────────────────────────────────
function GuestEmailPanel({
  roster,
  cabinId,
  sessionToken,
  onCover,
  overlineCls,
  headText,
  subText,
  faintText,
  glassPanel,
  onRosterUpdate,
}) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  // Folded by default — the panel is optional and shouldn't scream at guests.
  // Auto-expands when at least one roster member already saved an email, so
  // owners can see + edit at a glance without re-tapping the header.
  const savedCount = (roster || []).filter((r) => !!r.email).length;
  const [expanded, setExpanded] = useState(savedCount > 0);

  if (!Array.isArray(roster) || roster.length === 0) return null;
  if (!cabinId || !sessionToken) return null;

  const startEdit = (guestId, currentEmail) => {
    setEditingId(guestId);
    setDraft(currentEmail || "");
  };
  const cancelEdit = () => { setEditingId(null); setDraft(""); };

  const save = async (guestId, nextValue) => {
    setSaving(true);
    try {
      const { data } = await api.post("/public/guest-profile", {
        cabin_id: cabinId,
        session_token: sessionToken,
        guest_id: guestId,
        email: nextValue,
      });
      if (Array.isArray(data?.cabin_roster)) {
        onRosterUpdate(data.cabin_roster);
      }
      toast.success(nextValue ? "Email saved for this trip" : "Email removed");
      setEditingId(null);
      setDraft("");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save your email");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className={`mb-10 ${expanded ? "p-5 md:p-7" : "p-3 md:p-4"} ${
        onCover
          ? "bg-accent/25 backdrop-blur-md border-2 border-accent/70 text-white shadow-2xl"
          : "bg-accent/10 border-2 border-accent/60"
      }`}
      data-testid="cabin-email-panel"
    >
      {/* Folded header — always visible; acts as the toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center gap-3 text-left ${expanded ? "mb-4" : ""}`}
        data-testid="cabin-email-toggle"
        aria-expanded={expanded}
      >
        <Mail className={`h-5 w-5 md:h-6 md:w-6 flex-shrink-0 text-accent`} />
        <div className="flex-1 min-w-0">
          <div className={`font-display text-lg md:text-xl truncate ${headText}`}>
            Never miss a spa moment — email me
          </div>
          <div className={`text-[11px] md:text-xs mt-0.5 ${onCover ? "text-white/70" : "text-primary/70"}`}>
            {savedCount > 0
              ? `${savedCount} of ${roster.length} saved · tap to manage`
              : "GDPR-safe · auto-erased at trip end"}
          </div>
        </div>
        {savedCount > 0 && !expanded && (
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
              onCover ? "bg-accent/40 text-white" : "bg-accent/20 text-primary"
            }`}
            data-testid="cabin-email-saved-badge"
          >
            {savedCount} on
          </span>
        )}
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          } ${onCover ? "text-white/70" : "text-primary/70"}`}
        />
      </button>

      {expanded && (
        <>
          {/* Banner label — kept in the expanded body so the folded header stays compact */}
          <div className={`flex items-center gap-2 mb-2 ${onCover ? "text-white/90" : "text-primary"}`}>
            <ShieldCheck className="h-5 w-5 text-accent" />
            <span className="text-xs md:text-sm uppercase tracking-[0.25em] font-medium">
              GDPR notice · your data, protected
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <h3 className={`font-display text-2xl md:text-3xl leading-tight ${headText}`}>
                Email me my bookings — optional
              </h3>
              <p className={`text-xs md:text-sm mt-1 ${onCover ? "text-white/70" : "text-primary/70"}`}>
                For this trip only · auto-erased when the charter ends
              </p>
            </div>
          </div>

          {/* GDPR consent copy — Option B, kept in a distinct card so it reads as a policy notice */}
          <div
            className={`p-3 md:p-4 mb-4 ${
              onCover ? "bg-white/10 border border-white/20" : "bg-background/60 border border-accent/40"
            }`}
            data-testid="cabin-email-gdpr"
          >
            <p className={`text-sm md:text-base leading-relaxed ${onCover ? "text-white" : "text-primary/90"}`}>
              We only use your email to send you booking confirmations and reminders during your
              stay. It's stored in <strong>isolation</strong> — not tied to your name, cabin, or
              any personal data — and is <strong>automatically and permanently deleted when the
              charter ends</strong>. Only our automated notification system can read it. You can
              change or remove it anytime. <span className="italic">(GDPR-compliant)</span>
            </p>
          </div>

          <ul className="space-y-2" data-testid="cabin-email-list">
        {roster.map((r) => {
          const isEditing = editingId === r.guest_id;
          const hasEmail = !!(r.email && r.email.length);
          return (
            <li
              key={r.guest_id}
              className={`p-3 md:p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                onCover ? "bg-white/5 border border-white/15" : "border border-border"
              }`}
              data-testid={`cabin-email-row-${r.guest_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <User className="h-3.5 w-3.5 text-accent" />
                  <span className="font-medium truncate">{r.guest_name}</span>
                  {hasEmail && !isEditing && (
                    <span className={`text-[10px] uppercase tracking-widest ${overlineCls} flex items-center gap-1`}>
                      <Check className="h-3 w-3 text-accent" /> saved
                    </span>
                  )}
                </div>
                {isEditing ? (
                  <Input
                    type="email"
                    autoFocus
                    placeholder="you@example.com"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={saving}
                    className={`h-10 ${onCover ? "bg-white/10 border-white/25 text-white placeholder:text-white/40" : ""}`}
                    data-testid={`cabin-email-input-${r.guest_id}`}
                  />
                ) : (
                  <div className={`text-sm font-mono truncate ${hasEmail ? "" : "italic"} ${faintText}`}>
                    {r.email || "No email saved"}
                  </div>
                )}
              </div>
              <div className="flex gap-2 sm:flex-shrink-0">
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => save(r.guest_id, draft.trim())}
                      disabled={saving || (draft.trim() && !draft.includes("@"))}
                      className="rounded-full h-9"
                      data-testid={`cabin-email-save-${r.guest_id}`}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" /> Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEdit}
                      disabled={saving}
                      className="rounded-full h-9"
                      data-testid={`cabin-email-cancel-${r.guest_id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(r.guest_id, r.email)}
                      className={`rounded-full h-9 ${onCover ? "border-white/40 text-white hover:bg-white/10" : ""}`}
                      data-testid={`cabin-email-edit-${r.guest_id}`}
                    >
                      <Mail className="h-3.5 w-3.5 mr-1" />
                      {hasEmail ? "Change" : "Add email"}
                    </Button>
                    {hasEmail && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => save(r.guest_id, "")}
                        disabled={saving}
                        className={`rounded-full h-9 ${onCover ? "text-white/70 hover:bg-white/10" : "text-muted-foreground"}`}
                        data-testid={`cabin-email-clear-${r.guest_id}`}
                        title="Remove your email"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
        </>
      )}
    </section>
  );
}

